import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { isEmpty, isNil } from "lodash";
import React from "react";
import { UseQueryResult } from "react-query";

import CommodityAPI from "../api/Commodity.api";
import FieldVarietyAPI from "../api/FieldVariety.api";
import HarvestAPI from "../api/Harvest.api";
import TrialVarietyAPI from "../api/TrialVariety.api";
import { Commodity, Harvest, HarvestInput, Shipper, Subgrower } from "../types";
import Autosave from "./Autosave";
import CommodityCreatableAutocomplete from "./CommodityCreatableAutocomplete";
import PlantedVarietyList from "./PlantedVarietyList";
import PreliminaryWorkHarvestAttachedCheck from "./PreliminaryWorkHarvestAttachedCheck";
import SelectLotOrSublot from "./SelectLotOrSublot";
import ShipperCreatableAutocomplete from "./ShipperCreatableAutocomplete";
import SubGrowerCreatableAutocomplete from "./SubGrowerCreatableAutocomplete";

type Props = {
  harvest: Harvest;
  refetchHarvest: () => Promise<UseQueryResult>;
};

export default function HarvestScheduledForm({
  harvest,
  refetchHarvest,
}: Props) {
  const [projectedWetDate, setProjectedWetDate] = React.useState<Dayjs | null>(
    harvest.projectedWetDate ? dayjs(harvest.projectedWetDate) : null,
  );
  const [desiredNumAcres, setDesiredNumAcres] = React.useState<number>(
    harvest.desiredNumAcres || 0,
  );
  const [transplantDate, setTransplantDate] = React.useState<Dayjs | null>(
    harvest.transplantDate ? dayjs(harvest.transplantDate) : null,
  );
  const [estHarvestDate, setEstHarvestDate] = React.useState<Dayjs | null>(
    harvest.estHarvestDate ? dayjs(harvest.estHarvestDate) : null,
  );
  const [hasSubgrower, setHasSubgrower] = React.useState<boolean>(
    harvest.hasSubgrower,
  );
  const [commodity, setCommodity] = React.useState<Commodity>(
    harvest.commodity,
  );
  const [subgrower, setSubgrower] = React.useState<Subgrower>(
    harvest.subgrower,
  );
  const [shipper, setShipper] = React.useState<Shipper>(harvest.shipper);
  const [bedWidth, setBedWidth] = React.useState<number>(harvest.bedWidth || 0);
  const [seedLine, setSeedLine] = React.useState<number>(harvest.seedLine || 0);

  const harvestInput: HarvestInput = {
    id: harvest.id,
    bedWidth,
    commodityId: commodity?.id,
    desiredNumAcres,
    estHarvestDate: estHarvestDate
      ? estHarvestDate?.format("YYYY-MM-DD")
      : estHarvestDate,
    hasSubgrower,
    projectedWetDate: projectedWetDate
      ? projectedWetDate?.format("YYYY-MM-DD")
      : projectedWetDate,
    subgrowerId: subgrower?.id,
    shipperId: shipper?.id,
    seedLine,
    transplantDate: transplantDate
      ? transplantDate?.format("YYYY-MM-DD")
      : transplantDate,
  };

  // await refetchHarvest();
  const { mutateAsync } = HarvestAPI.useUpdateAuto();

  const { mutateAsync: mutateDateEstimator } =
    CommodityAPI.useWetVsHarvestDateEstimator();

  const handleProjectedWetDate = async (value: null | Dayjs) => {
    setProjectedWetDate(value);
    if (commodity?.id && isNil(estHarvestDate) && !isNil(value)) {
      const result = await mutateDateEstimator({
        commodityId: commodity?.id,
        estHarvestDate: undefined,
        projectedWetDate: value?.format("YYYY-MM-DD"),
      });
      if (!isNil(result["estHarvestDate"])) {
        setEstHarvestDate(dayjs(result["estHarvestDate"]));
      }
    }
  };

  const handleEstHarvestDate = async (value: null | Dayjs) => {
    setEstHarvestDate(value);
    if (commodity?.id && isNil(projectedWetDate) && !isNil(value)) {
      const result = await mutateDateEstimator({
        commodityId: commodity?.id,
        estHarvestDate: value?.format("YYYY-MM-DD"),
        projectedWetDate: undefined,
      });
      if (!isNil(result["projectedWetDate"])) {
        setProjectedWetDate(dayjs(result["projectedWetDate"]));
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <Autosave
        data={harvestInput}
        patchApiCall={mutateAsync}
        showPopup={true}
      />

      <SelectLotOrSublot harvest={harvest} refetchHarvest={refetchHarvest} />

      <PreliminaryWorkHarvestAttachedCheck
        harvestId={harvest.id}
        refetchHarvest={refetchHarvest}
      />

      <Divider>
        <Chip label="Basic Information" />
      </Divider>
      <Box
        sx={{
          gap: "1rem",
          display: "flex",
          flexDirection: { xs: "column", md: "row", lg: "row" },
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexShrink: 1 }}>
          <TextField
            label="Desired Number of Acres"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            value={desiredNumAcres}
            onChange={(e) => setDesiredNumAcres(parseFloat(e.target.value))}
            fullWidth
          />
        </Box>
        <DatePicker
          label="Projected Wet Date"
          value={projectedWetDate}
          onChange={handleProjectedWetDate}
          slotProps={{
            field: { clearable: true },
          }}
        />
        <Box
          sx={{
            display: "flex",
            flex: 1,
          }}
        >
          <CommodityCreatableAutocomplete
            setCurrentCommodity={(commodity: Commodity) =>
              setCommodity(commodity)
            }
            commodity={commodity}
          />
        </Box>
      </Box>
      <Box
        sx={{
          gap: "1rem",
          display: "flex",
          flexDirection: { xs: "column", md: "row", lg: "row" },
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <DatePicker
            label="Transplant Date"
            value={transplantDate}
            onChange={(value) => setTransplantDate(value)}
            sx={{ width: "100%" }}
            slotProps={{
              field: { clearable: true },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <DatePicker
            label="Est. Harvest Date"
            value={estHarvestDate}
            onChange={handleEstHarvestDate}
            sx={{ width: "100%" }}
            slotProps={{
              field: { clearable: true },
            }}
          />
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ShipperCreatableAutocomplete
            setCurrentShipper={(shipper: Shipper) => setShipper(shipper)}
            shipper={shipper}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={hasSubgrower}
                  onChange={(event) => {
                    setHasSubgrower(event.target.checked);
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Is this from a subgrower?"
            />
          </FormGroup>
          {hasSubgrower && (
            <SubGrowerCreatableAutocomplete
              setCurrentSubGrower={(subgrower: Subgrower) =>
                setSubgrower(subgrower)
              }
              subgrower={subgrower}
            />
          )}
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", flex: 1, gap: "1rem", flexDirection: "row" }}>
        <TextField
          label="Bed Width"
          type="number"
          InputLabelProps={{
            shrink: true,
          }}
          fullWidth
          value={bedWidth}
          onChange={(e) => setBedWidth(parseInt(e.target.value))}
        />
        <TextField
          label="Seed Line"
          type="number"
          InputLabelProps={{
            shrink: true,
          }}
          value={seedLine}
          onChange={(e) => {
            const str = e.target.value;
            if (isEmpty(str)) {
              setSeedLine(0);
            } else {
              setSeedLine(parseInt(str));
            }
          }}
          fullWidth
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flex: 1,
        }}
      >
        <PlantedVarietyList
          harvestId={harvest.id}
          modelAPI={FieldVarietyAPI}
          type={"FieldVariety"}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
        }}
      >
        <PlantedVarietyList
          harvestId={harvest.id}
          modelAPI={TrialVarietyAPI}
          type={"TrialVariety"}
        />
      </Box>
    </Box>
  );
}
